import { NgClass } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject, output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatSelectChange, MatSelectTrigger } from '@angular/material/select';

import { AnchorDirective, CurrencyPipe, DeviceService, DynamicHtmlDirective, NavigationService } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import * as braintree from 'braintree-web';
import { NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { Subscription } from 'rxjs';

import { AppConstants, BrandType, ChannelType, Config, Country, CurrencyCode, InputType, ProductType, QDType } from '../../../../config/appConstants';
import { UrlConstants } from '../../../../config/urlConstants';
import { DataGrid, PaymentOption, SessionInfo } from '../../../../model/common.model';
import { DepositInfo } from '../../../../model/depositInfo.model';
import { MessageType } from '../../../../model/message.model';
import { QDInstrument } from '../../../../model/qdInstrument.model';
import { BehaviorDataService } from '../../../../service/behaviorData.service';
import { DataService } from '../../../../service/data.service';
import { DepositService } from '../../../../service/deposit.service';
import { DepositRedirectService } from '../../../../service/depositRedirect.service';
import { GtmService } from '../../../../service/gtm.service';
import { LoaderService } from '../../../../service/loader.service';
import { OptionListService } from '../../../../service/optionList.service';
import { UserDataService } from '../../../../service/userDataService';
import { GVCAmountGridButtonsComponent } from '../../gvc/amount-buttons/amount-buttons.component';
import { GVCAmountGridComponent } from '../../gvc/amount-grid/amount-grid.component';
import { NumericKeyboardComponent } from '../../gvc/numeric-keyboard/numeric-keyboard.component';
import { LCGAmountGridButtonsComponent } from '../../lcg/amount-buttons/amount-buttons.component';
import { LCGAmountGridComponent } from '../../lcg/amount-grid/amount-grid.component';
import { BonusInputComponent } from '../bonus-input/bonus-input.component';
import { CCInputComponent } from '../cc-input/cc-input.component';
import { PasswordBlockComponent } from '../cc-input/password-block/password-block.component';
import { DepositLimitMessageComponent } from '../deposit-limit-message/deposit-limit-message.component';
import { Flash1VoucherInputComponent } from '../flash1voucher/flash1voucher-input.component';
import { MessageComponent } from '../messages/message-component';
import { ThresholdLimitMessagesComponent } from '../threshold-limit-messages/threshold-limit-messages.component';
import { OptionContentComponent } from './option-content/option-content.component';

@Component({
    selector: 'app-options-list',
    templateUrl: './options-list.component.html',
    styleUrls: ['./options-list.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatSelect,
        MatSelectTrigger,
        OptionContentComponent,
        NgxPopperjsModule,
        MatOption,
        MessageComponent,
        NgClass,
        LCGAmountGridComponent,
        CCInputComponent,
        Flash1VoucherInputComponent,
        LCGAmountGridButtonsComponent,
        GVCAmountGridComponent,
        NumericKeyboardComponent,
        GVCAmountGridButtonsComponent,
        ThresholdLimitMessagesComponent,
        DepositLimitMessageComponent,
        BonusInputComponent,
        PasswordBlockComponent,
        TranslateModule,
        CurrencyPipe,
        AnchorDirective,
        DynamicHtmlDirective,
    ],
})
export class OptionsListComponent implements OnInit, OnDestroy {
    private navigationService = inject(NavigationService);
    @Input() instrumentFormGroup: FormGroup;
    paymentMethod = output<string>();
    subscriptions$: Subscription[] = [];
    optionsList: QDInstrument[];
    inputType: string;
    ccFamily = AppConstants.CCFAMILY;
    prepaidCards = AppConstants.PREPAIDCARDS;
    initialValue: string;
    isUSLabel: boolean;
    usedOptions: PaymentOption[] = [];
    ccDepositLimitCheckEnabled: boolean;
    ccCurrentDepositLimit: number;
    isInstantEchecks: boolean;
    rgLimitData: any;
    depositInfo: DepositInfo;
    selectedPaymentMethod: PaymentOption | undefined;
    gridData: DataGrid;
    showKeyboard: boolean = false;
    controlName: string;
    currentValue: string;
    maxLength: number;
    hideAllDepositOptions: boolean;
    channelId: any;
    defaultCurrency: boolean = true;
    maxFailDepEnhancementEnabled: boolean = false;
    isESRiskPlayer: boolean;
    isESIntensivePlayer: boolean;
    liveChatRiskPlayerUrl: string;
    moreInfoIntensePlayerLink: string;
    isMobile: boolean;
    currencyUSD: string = 'USD';
    sessionInfo: SessionInfo;
    showDepositLimitMsg: boolean;
    public get messageType(): typeof MessageType {
        return MessageType;
    }

    constructor(
        public depositService: DepositService,
        private dataService: DataService,
        private userDataService: UserDataService,
        private behaviorData: BehaviorDataService,
        private depositRedirectService: DepositRedirectService,
        private loaderService: LoaderService,
        public optionListService: OptionListService,
        private GTMService: GtmService,
        private deviceService: DeviceService,
    ) {
        this.optionsList = optionListService.optionList;
        this.depositInfo = this.depositService.depositInfo;
        this.isMobile = this.deviceService.isMobile;
    }

    ngOnInit() {
        this.sessionInfo = this.dataService.sessionInfo;
        const brandId = this.sessionInfo?.brandId;
        this.channelId = this.sessionInfo?.channelId;
        const stake = +this.sessionInfo?.stake;
        const lang = this.sessionInfo?.LANG_ID ?? 'en';
        this.hideAllDepositOptions =
            [BrandType.CORAL, BrandType.LADBROKEUK, BrandType.LADBROKEDE].includes(brandId) && ChannelType.WC === this.channelId && stake >= 0;
        this.inputType = this.optionsList[0]?.paymentMethodInfo?.paymentMethod;
        this.paymentMethod.emit(this.inputType);
        this.initialValue = this.optionsList[0]?.instrumentNumber;
        this.liveChatRiskPlayerUrl = `${this.userDataService?.userData?.configParams?.merchant_hostname}/${lang}${UrlConstants.LIVE_CHAT_ES_LINK}`;
        this.moreInfoIntensePlayerLink = `${this.userDataService?.userData?.configParams?.merchant_hostname}/${lang}${UrlConstants.MORE_INFO_ES_LINK}`;

        if (this.optionsList?.length > 0) {
            this.usedOptions = [];
            this.optionsList.map((option) => {
                const newOption = new PaymentOption();
                const paymentMethod = option?.paymentMethodInfo?.paymentMethod;
                newOption.imgPath = AppConstants.IMAGE_PATH_PAYMENT_OPTIONS + paymentMethod?.toLowerCase() + '.svg';
                newOption.isESRiskPlayer = option?.paymentAccountInfo?.isESRiskPlayer == 'Y';
                newOption.ESIntensiveGreyOut = option?.paymentAccountInfo?.ESIntensiveGreyOut == true;
                newOption.instrumentNumber = option?.instrumentNumber;
                newOption.paymentMethod = paymentMethod?.toLowerCase();
                newOption.paymentText = paymentMethod?.charAt(0)?.toUpperCase() + paymentMethod?.substring(1)?.toLowerCase();
                newOption.sportsOnly = option?.sportsOnly;
                newOption.bankAccountsOnly = option?.bankAccountsOnly;
                newOption.userMaxFailDepStatus = option?.details?.userMaxFailDepStatus;
                const expiryTime = Number(newOption?.userMaxFailDepStatus?.expiryTime) || 1;
                if (newOption?.userMaxFailDepStatus) {
                    setTimeout(() => {
                        newOption.userMaxFailDepStatus.blocked = false;
                    }, expiryTime * 1000);
                }
                this.usedOptions.push(newOption);
            });

            if (this.usedOptions?.length > 0) {
                this.selectedPaymentMethod = this.usedOptions[0];
            }
        }

        this.buildFormGroups();
        this.optionListService.selectedOption = this.optionsList[0];
        this.depositService.setCurrencyConversion(this.optionListService?.selectedOption);
        this.maxFailDepEnhancementEnabled = this.userDataService?.userData?.configParams?.maxFailDepEnhancementEnabled === 'Y';
        this.isUSLabel = this.userDataService?.userData?.userInfo?.addressInfo?.country === Country.US;
        this.ccDepositLimitCheckEnabled = this.userDataService?.userData?.configParams?.isCreditCardDepositLimitCheckEnabled === Config.YES;
        if (this.ccDepositLimitCheckEnabled) {
            this.ccCurrentDepositLimit = this.userDataService?.userData?.configParams?.currentCreditCardDepositLimit;
        }
        if (this.depositInfo?.isBelgiumRgDecreeEnabled) {
            const rgLimitInterval = setInterval(() => {
                this.rgLimitData = this.dataService?.RgLimitData;
                if (this.rgLimitData[0]?.rgLimitInfo?.length > 0) {
                    clearInterval(rgLimitInterval);
                }
            }, 100);
            setTimeout(() => {
                if (rgLimitInterval && !this.rgLimitData?.rglimitInfo) {
                    clearInterval(rgLimitInterval);
                }
            }, 1000);
        }
        if (this.isESIntensivePlayer) {
            this.GTMService.intensivePlayerMsgLoad(this.inputType, 'betslip quick deposit');
        }
        this.setInputTypeDefaults();
        this.getBehaviorData();
    }

    get amountFieldFormGroup(): FormGroup {
        return this.instrumentFormGroup?.get('amountFieldFormGroup') as FormGroup;
    }

    get instrumentInputFormGroup(): FormGroup {
        return this.instrumentFormGroup?.get('instrumentInputFormGroup') as FormGroup;
    }

    get popperTrigger(): NgxPopperjsTriggers {
        return this.isMobile ? NgxPopperjsTriggers.click : NgxPopperjsTriggers.hover;
    }

    get popperPlacementBottom(): NgxPopperjsPlacements {
        return NgxPopperjsPlacements.BOTTOM;
    }

    buildFormGroups() {
        this.instrumentFormGroup?.addControl('instrumentFormCtrl', new FormControl(this.usedOptions[0]));
        this.instrumentFormGroup?.addControl('instrumentInputFormGroup', new FormGroup({}));
        this.instrumentFormGroup?.addControl('amountFieldFormGroup', new FormGroup({}));
        this.instrumentFormGroup?.addControl('productPreferenceFormGroup', new FormGroup({}));
    }

    onChange(instrumentName: MatSelectChange) {
        this.behaviorData.changeCVV2('');
        this.depositService.showDepositSuccess.set(false);
        this.depositService.showDepositFailure.set(false);
        this.selectedPaymentMethod = this.usedOptions?.find((option) => option?.instrumentNumber === instrumentName?.value?.instrumentNumber);
        if (this.depositService?.QDType === QDType.GVCBETSLIP) {
            this.GTMService.raiseFieldTrackingGTMEvent('Field - Completed', 'Payment method - Dropdown');
        }
        if (instrumentName?.value === InputType.allDepositOptions) {
            if (this.depositService?.QDType === QDType.GVCBETSLIP) {
                this.optionListService.redirectToMainCashier('&type=options');
            } else {
                if (
                    [ChannelType.WC, ChannelType.VC, ChannelType.FC, ChannelType.MC].includes(this.channelId) ||
                    this.sessionInfo?.productId === ProductType.BINGO
                ) {
                    parent.postMessage('action=close&showAllOptions=true&parent=' + window.location.ancestorOrigins[0], '*');
                } else {
                    this.optionListService.redirectToMainCashier('&type=options');
                }
            }
        } else {
            this.instrumentFormGroup?.removeControl('instrumentInputFormGroup');
            this.instrumentFormGroup?.addControl('instrumentInputFormGroup', new FormGroup({}));
            this.instrumentFormGroup?.removeControl('productPreferenceFormGroup');
            this.instrumentFormGroup?.addControl('productPreferenceFormGroup', new FormGroup({}));
            const selectedOption = this.optionsList?.find((option) => option?.instrumentNumber === instrumentName?.value?.instrumentNumber);
            if (selectedOption) {
                const paymentMethodNameMessage = {
                    eventName: 'selectedPaymentMethod',
                    eventData: {
                        paymentMethodName: selectedOption?.paymentMethodInfo?.paymentMethod,
                    },
                };
                this.depositService.postMessageToParent(paymentMethodNameMessage, '*');
                this.inputType = selectedOption?.paymentMethodInfo?.paymentMethod;
                this.paymentMethod.emit(this.inputType);
                this.optionListService.selectedOption = selectedOption;
                this.setInputTypeDefaults();
                this.depositService.paypalDirectBadFunding = null;
            }
        }
        this.depositService.resizeCashier();
    }

    setInputTypeDefaults() {
        if (this.optionListService?.selectedOption) {
            // clearing previous transaction details on instrument change when popup blocer is active
            if (this.depositRedirectService.popupBlocked) {
                this.clearPrevTransData();
            }
            this.showChangeCurrency();
            this.depositService.depositInfo.paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
            this.depositService.depositInfo.amountInfo.nativeCurrency =
                this.optionListService?.selectedOption?.currencyInfo?.defaultCurr ||
                this.optionListService?.selectedOption?.currencyInfo?.currencyList[0];
            this.depositService.depositInfo.paymentAccountInfo.paymentAccountId =
                this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountId;
            this.depositService.setCurrencyConversion(this.optionListService?.selectedOption);
            if (this.ccFamily.includes(this.inputType)) {
                this.depositService.depositInfo.paymentAccountInfo.ccId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
                this.isESIntensivePlayer = this.optionListService?.selectedOption?.paymentAccountInfo?.ESIntensiveGreyOut == true;
            }
            if (this.inputType === InputType.PAYPAL) {
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
            }
            if (this.inputType === InputType.CANADIANEFT) {
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
            }
            if (this.inputType === InputType.VENMO) {
                this.depositService.depositInfo.paymentAccountInfo.paymentAccountId =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountId;
                this.depositService.depositInfo.paymentAccountInfo.venmo_firstName = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_lastName = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_email = this.optionListService?.selectedOption?.instrumentNumber;
                this.depositService.depositInfo.paymentAccountInfo.venmo_phone = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_nonce = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_collectedDeviceData = null;
                this.depositService.depositInfo.paymentAccountInfo.venmo_payment_data =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.paymentAccountId;
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
            }
            if (this.inputType === InputType.INSTANTECHECKS) {
                this.isInstantEchecks = true;
                this.depositService.depositInfo.paymentAccountInfo.accountId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
                this.depositService.depositInfo.paymentAccountInfo.vipPreferredAccountNumber =
                    this.optionListService?.selectedOption?.paymentAccountInfo?.vipPreferredAccountNumber;
            }
            if (this.prepaidCards.includes(this.inputType)) {
                this.depositService.depositInfo.playPlusCashId = this.optionListService?.selectedOption?.playPlusCashId;
            }

            if (this.inputType === InputType.VENMO) {
                this.getVemoCollectedDeviceData();
            }
            if (this.inputType === InputType.PAYPALDIRECT) {
                this.depositService.depositInfo.paymentAccountInfo.instrumentId = this.optionListService?.selectedOption?.paymentAccountInfo?.id;
                if (this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeToken) {
                    this.getDeviceDataBrainTree(this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeToken);
                }
            }
            if (
                this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeSDKEnable == 'Y' &&
                this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeAuthKey
            ) {
                this.getDeviceDataBrainTree(this.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeAuthKey);
            }
            this.gridData = {
                nativeAmountString: '',
                activeItem: false,
                amountGridIncrementalValue: 5,
                isGridSelected: false,
            };
        }
        this.fillAmount(null);
        if (this.inputType === InputType.FLASH1VOUCHER) {
            this.depositService.depositInfo.amountInfo.nativeCurrency = CurrencyCode.ZAR;
            this.depositService.depositInfo.amountInfo.nativeAmountString = '0';
        }
        this.isESRiskPlayer = this.optionListService?.selectedOption?.paymentAccountInfo?.isESRiskPlayer == 'Y';
    }

    showChangeCurrency() {
        const defaultCurr = this.optionListService?.selectedOption?.currencyInfo?.defaultCurr;
        const accountCurr = this.userDataService?.userData?.userInfo?.accountCurrency;
        this.depositService.depositInfo.amountInfo.nativeCurrency = defaultCurr;
        this.defaultCurrency = defaultCurr === accountCurr ? true : false;
    }

    moreInfoLink() {
        this.GTMService.intensivePlayerMsgClickOnMoreInfo('betslip quick deposit', this.moreInfoIntensePlayerLink);
        this.navigationService.goTo(this.moreInfoIntensePlayerLink, { forceReloadTarget: 'top' });
    }

    clearPrevTransData() {
        this.depositRedirectService.popupBlocked = false;
        this.loaderService.depositRedirectInProgress.set(false);
        this.depositRedirectService.resetdepositRedirectInfo();
        this.loaderService.isLoading.next(false);
    }

    getDeviceDataBrainTree(brainTreeToken: any) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        // let client;
        braintree.client
            .create({
                authorization: brainTreeToken,
            })
            .then(function (clientInstance: any) {
                // client = clientInstance;
                return braintree.dataCollector.create({
                    client: clientInstance,
                });
            })
            .then(function (dataCollectorInstance: any) {
                if (self.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeSDKEnable == 'Y') {
                    self.depositService.depositInfo.paymentAccountInfo.isBTSDKSuccess = 'Y';
                    self.depositService.depositInfo.paymentAccountInfo.brainTreeDeviceData = dataCollectorInstance?.rawDeviceData
                        ? dataCollectorInstance?.rawDeviceData?.correlation_id
                        : '';
                    self.depositService.depositInfo.paymentAccountInfo.customerBrowser = self.detectBrowser();
                } else {
                    self.depositService.depositInfo.paymentAccountInfo.deviceData = dataCollectorInstance?.rawDeviceData
                        ? dataCollectorInstance?.rawDeviceData?.correlation_id
                        : '';
                }
            })
            .catch(() => {
                self.optionListService?.selectedOption?.paymentAccountInfo?.brainTreeSDKEnable == 'Y'
                    ? (self.depositService.depositInfo.paymentAccountInfo.isBTSDKSuccess = 'N')
                    : '';
            });
    }
    detectBrowser() {
        const userAgent = navigator.userAgent;
        if (userAgent.indexOf('Edg') > -1) {
            return 'Microsoft Edge';
        } else if (userAgent.indexOf('Chrome') > -1) {
            return 'Chrome';
        } else if (userAgent.indexOf('Firefox') > -1) {
            return 'Firefox';
        } else if (userAgent.indexOf('Safari') > -1) {
            return 'Safari';
        } else if (userAgent.indexOf('Opera') > -1) {
            return 'Opera';
        } else if (userAgent.indexOf('Trident') > -1 || userAgent.indexOf('MSIE') > -1) {
            return 'Internet Explorer';
        }

        return 'Unknown';
    }

    getVemoCollectedDeviceData() {
        const authorization = this.optionListService?.selectedOption?.paymentAccountInfo?.VENMO_CLIENT_AUTHORIZATION;
        braintree.client
            .create({
                authorization: authorization,
            })
            .then((clientInstance: any) => {
                return Promise.all([
                    braintree.dataCollector.create({
                        client: clientInstance,
                        paypal: true,
                    }),
                ]);
            })
            .then((results: any) => {
                if (results && results[0]) {
                    const dataCollectorInstance = results[0];
                    const collectedDeviceData = dataCollectorInstance?.rawDeviceData?.correlation_id || null;
                    this.depositService.depositInfo.paymentAccountInfo.venmo_collectedDeviceData = collectedDeviceData;
                }
            });
    }

    fillAmount(data: any) {
        if (data) {
            this.gridData = data;
            this.depositService.depositInfo.amountInfo.nativeAmountString = data?.nativeAmountString;
        }
    }

    showHideKeyboard(event: any) {
        this.showKeyboard = event.isVisible;
        this.controlName = event.name;
        this.currentValue = event.currentValue;
        this.maxLength = event.maxLength;
        this.depositService.resizeCashier();
    }
    depositLimitCheckEnabled(event: boolean) {
        this.showDepositLimitMsg = event;
    }

    handleKeyboardData(data: string) {
        if (this.controlName === 'amount') {
            this.behaviorData.changeEnteredAmount(data);
        } else if (this.controlName === 'cvv2') {
            this.behaviorData.changeCVV2(data);
        }
    }

    keyboardStatus(data: boolean) {
        this.showKeyboard = !data;
    }

    getBehaviorData() {
        this.subscriptions$.push(
            this.behaviorData.changeMaxFailDepEnhancement.subscribe((res: any) => {
                if (res?.blocked && res?.expiryTime) {
                    const findOptionByInstrumentNumber = (options: any, instrumentNumber: any) => {
                        return options.find((option: any) => option?.instrumentNumber === instrumentNumber);
                    };
                    const mfdePaymentOption = findOptionByInstrumentNumber(this.usedOptions, this.selectedPaymentMethod?.instrumentNumber);
                    const mfdeQDinstrument = findOptionByInstrumentNumber(this.optionsList, this.selectedPaymentMethod?.instrumentNumber);
                    if (mfdeQDinstrument && mfdePaymentOption) {
                        if (!mfdeQDinstrument.details) {
                            mfdeQDinstrument.details = {};
                        }
                        mfdeQDinstrument.details.userMaxFailDepStatus = res;
                        mfdePaymentOption.userMaxFailDepStatus = res;
                        if (res?.blocked) {
                            setTimeout(() => {
                                res.blocked = false;
                            }, res.expiryTime * 1000);
                        }
                    }
                    if (!this.usedOptions.find((option) => !option?.userMaxFailDepStatus?.blocked)) {
                        this.optionListService.redirectToMainCashier('&type=options');
                    }
                }
            }),
        );
    }

    stopPropogation(event: any) {
        event.preventDefault();
        event.stopPropagation();
    }
    updateVoucher() {
        // logic
    }

    ngOnDestroy() {
        this.depositService.showDepositInput.set(false);
        this.subscriptions$.map((subscription) => subscription.unsubscribe());
    }
}
